import { useState } from 'react'

import BecomeValidatorModal from './BecomeValidatorModal'
import ChooseBecomeValidatorFlowModal from './BecomeValidatorModal/ChooseBecomeValidatorFlowModal'
import ChangeOwnerModal from './ChangeOwnerModal'
import CloseNftModal from './CloseNftModal'
import DelegateModal from './DelegateModal'
import MintModal from './MintModal'
import RedelegateModal from './RedelegateModal'
import TopUpModal from './TopUpModal'
import UndelegateModal from './UndelegateModal'
import UnjailModal from './UnjailModal'
import WithdrawalModal from './WithdrawalModal'

export type ActiveModal =
  | null
  | 'mint'
  | 'become-validator'
  | 'delegate'
  | 'undelegate'
  | 'redelegate'
  | 'withdraw'
  | 'top-up'
  | 'change-owner'
  | 'close'
  | 'unjail'

interface NftActionModalsProps {
  activeModal: ActiveModal
  setActiveModal: (modal: ActiveModal) => void
  nftAddress?: string
  becomeChoiceOpen: boolean
  setBecomeChoiceOpen: (open: boolean) => void
}

export const NftActionModals = ({
  activeModal,
  setActiveModal,
  nftAddress,
  becomeChoiceOpen,
  setBecomeChoiceOpen,
}: NftActionModalsProps) => {
  const [becomeValidatorMode, setBecomeValidatorMode] = useState<
    'shards' | 'amount'
  >('shards')
  const [candidateAddresses, setCandidateAddresses] = useState<string[]>([])

  const closeModal = () => setActiveModal(null)

  const renderActiveModal = () => {
    if (!activeModal) return null

    const isNftScopedModal = !['mint', 'become-validator', 'unjail'].includes(
      activeModal,
    )
    if (!nftAddress && isNftScopedModal) return null

    switch (activeModal) {
      case 'mint':
        return <MintModal open onClose={closeModal} />

      case 'become-validator':
        return (
          <BecomeValidatorModal
            open
            selectedAddresses={candidateAddresses}
            onToggleAddress={(addr: string) =>
              setCandidateAddresses(prev =>
                prev.includes(addr)
                  ? prev.filter(a => a !== addr)
                  : [...prev, addr],
              )
            }
            onClose={() => {
              setCandidateAddresses([])
              closeModal()
            }}
            onBackToFlow={() => {
              setCandidateAddresses([])
              closeModal()
            }}
            mode={becomeValidatorMode}
            onSuccess={() => {
              setCandidateAddresses([])
              closeModal()
              setBecomeChoiceOpen(false)
            }}
          />
        )

      case 'delegate':
        return (
          <DelegateModal open nftAddress={nftAddress!} onClose={closeModal} />
        )

      case 'undelegate':
        return (
          <UndelegateModal open nftAddress={nftAddress!} onClose={closeModal} />
        )

      case 'redelegate':
        return (
          <RedelegateModal open nftAddress={nftAddress!} onClose={closeModal} />
        )

      case 'withdraw':
        return (
          <WithdrawalModal open nftAddress={nftAddress!} onClose={closeModal} />
        )

      case 'top-up':
        return <TopUpModal open nftAddress={nftAddress!} onClose={closeModal} />

      case 'change-owner':
        return (
          <ChangeOwnerModal
            open
            nftAddress={nftAddress!}
            onClose={closeModal}
          />
        )

      case 'close':
        return (
          <CloseNftModal open nftAddress={nftAddress!} onClose={closeModal} />
        )

      case 'unjail':
        return <UnjailModal open onClose={closeModal} />

      default:
        return null
    }
  }

  return (
    <>
      <ChooseBecomeValidatorFlowModal
        open={becomeChoiceOpen}
        onOpenChange={isOpen => {
          if (!isOpen && !activeModal) setBecomeChoiceOpen(false)
          if (!isOpen) setBecomeChoiceOpen(false)
        }}
        onSelect={mode => {
          setBecomeValidatorMode(mode)
          setActiveModal('become-validator')
        }}
      />

      {renderActiveModal()}
    </>
  )
}
